import { differenceInYears } from "date-fns";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { SourceTransitionalState } from "../components/fields/PPRadioGroup";
import {
  AppContextType,
  ListViewContextType,
  ListViewsContextType,
} from "../context/ApplicationContext";
import { loadFormConfiguration } from "../store/load-form-slice";
import { fetchFipsCodes } from "../store/fips-code-slice";
import store from "../store/store";
import { evaluateExpression } from "./parser";
import { isEqual, cloneDeep } from "lodash";
import { logger } from "../utils/logger";

let map = new Map<string, (...args: any[]) => void>();

export const funcExecutor = (
  funcName: string,
  args: any[],
  getValues: UseFormGetValues<FieldValues>,
  setValue: UseFormSetValue<FieldValues>,
  appContext: AppContextType,
  sourceTranistionalState: SourceTransitionalState | undefined = {
    previousValue: undefined,
    currentValue: undefined,
  },
  lstContext: ListViewsContextType | undefined = undefined,
  setLstContext:
    | React.Dispatch<React.SetStateAction<ListViewsContextType>>
    | undefined = undefined,
  queryParams: URLSearchParams | undefined = undefined,
  setAppContext:
    | React.Dispatch<React.SetStateAction<AppContextType>>
    | undefined = undefined
) => {
  const values: FieldValues = getValues();

  const setValueWhen = (targetField: string, value: string, when: string) => {
    const argArray: string[] = when.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    if (result) {
      setRadioValueAndDispatchEvent(targetField, value);
    }
  };

  const setRadioValueAndDispatchEvent = (
    targetFieldId: string,
    value: string | number
  ): void => {
    let input = Array.from(document.getElementsByName(targetFieldId)).filter(
      (v) => {
        return (v as HTMLInputElement).value === value;
      }
    )[0] as HTMLInputElement;

    input.click();
    appContext.fieldState[targetFieldId] = true;
  };

  const setValueAndDispatchEvent = (
    targetFieldId: string,
    value: string | number
  ): void => {
    let input = document.getElementById(targetFieldId);
    let nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value"
    )?.set;
    nativeInputValueSetter?.call(input, value);
    let ev2 = new Event("input", { bubbles: true });
    input?.dispatchEvent(ev2);
  };

  const calculateAge = (
    targetField: string,
    sourceField: string,
    values: FieldValues,
    setValue: UseFormSetValue<FieldValues>
  ): void => {
    const dob: Date = new Date(values[sourceField]);
    setValue(targetField, differenceInYears(Date.now(), dob));
    setValueAndDispatchEvent(targetField, differenceInYears(Date.now(), dob));
  };

  const resetField = (targetField: string) => setValue(targetField, undefined);

  const resetFields = (
    targetFields: string[],
    resetWhen: string | undefined = undefined
  ): void => {
    let doReset: boolean = false;
    if (resetWhen == undefined) doReset = true;
    else if (evaluateExpression(resetWhen.split(" "), values)) doReset = true;
    if (doReset)
      targetFields.forEach((targetField: string) => resetField(targetField));
  };

  const toggleCheckbox = (
    targetField: string,
    toggleWhen: string | undefined = undefined
  ) => {
    let doToggle: boolean =
      !toggleWhen || evaluateExpression(toggleWhen.split(" "), values);

    if (doToggle) {
      appContext.checkboxChecked.checked[targetField] =
        !appContext.checkboxChecked.checked[targetField];
    }
  };

  const resetErrorState = (targetFields: string[]) => {
    targetFields.forEach((field: string) => {
      appContext.fieldState[field] = true;
    });
  };

  const hideFieldsWhen = (
    targetFields: string[],
    hidewhen: string,
    sourceField: string
  ): void => {
    const argArray: string[] = hidewhen.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    targetFields.forEach(
      (targetField: string) => (appContext.fieldHide[targetField] = result)
    );
  };

  const copyFieldsWhen = (
    targetFields: string[],
    sourceFields: string[],
    copyWhen: string
  ): void => {
    const argArray: string[] = copyWhen.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    if (sourceFields.length == targetFields.length) {
      sourceFields.forEach((sourceField: string, index: number) => {
        setValue(targetFields[index], values[sourceField]);
      });
    }
  };

  const disableEnableFieldsWhen = (
    targetFields: string[],
    disableWhen: string,
    enable: string
  ): void => {
    const argArray: string[] = disableWhen.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    targetFields.forEach((targetField: string) => {
      appContext.fieldDisable[targetField] = /^true$/i.test(enable)
        ? !result
        : result;
    });
  };

  const setTextFieldValueWhen = (
    sourceField: string,
    targetField: string,
    linkWhen: string
  ): void => {
    const argArray: string[] = linkWhen.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    if (result) setValue(targetField, values[sourceField]);
  };

  const disableEnableStepWhen = (sectionId: string, disableWhen: string) => {
    const argArray: string[] = disableWhen.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    appContext.sectionState.hideState[sectionId] = result;
  };

  const setMinimumNumberOfRows = (...args: any[]): void => {
    if (args.length > 0 && args.length % 2 == 2) return;

    if (args.length > 0 && lstContext) {
      const sectionId: string = args.shift();
      while (args.length > 0) {
        const when: string[] = args.shift().split(" ");
        const value: string = args.shift();
        const result: boolean = evaluateExpression(when.slice(), values);
        if (result) {
          lstContext[sectionId].minimumNumberOfRows = +value;
          return;
        }
      }
    }
  };

  // loadForm needs refactoring to accomodate ListView loads
  // loadForm needs refactoring to set field values other than strings

  const loadForm = (fieldId: string): void => {
    if (fieldId) {
      store.dispatch(
        loadFormConfiguration(
          queryParams ?? new URLSearchParams(),
          getValues(fieldId),
          setValue
        )
      );
    }
  };

  const clearListView = (sectionId: string, when: string): void => {
    const argArray: string[] = when.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    if (result) {
      let newListsContext = { ...lstContext };
      let tmpListCtx: ListViewContextType | undefined =
        newListsContext[sectionId];
      if (tmpListCtx) {
        tmpListCtx.errorState.message = undefined;
        tmpListCtx.errorState.state = false;
        tmpListCtx.dataModel = { uiModel: [], submitRecordModel: [] };
      }
      if (setLstContext) setLstContext({ ...newListsContext });
    }
  };

  const setCompleteStepState = (
    sectionId: string,
    state: string,
    when: string
  ): void => {
    const whenArray: string[] = when.split(" ");
    const result: boolean = evaluateExpression(whenArray, values);
    if (result) {
      const stateArray: string[] = state.split(" ");
      const isComplete: boolean = evaluateExpression(stateArray, values);
      appContext.sectionState.completeState[sectionId] = isComplete;
    }
  };
  const filterComboBoxByCountyFIPS = (
    targetField: string,
    sourceField: string
  ): void => {
    // Add immediate debug logging for timing analysis
    logger.timing(`filterComboBoxByCountyFIPS called at ${Date.now()}`);
    logger.timing(`Form values snapshot:`, values);
    logger.timing(`Source field ${sourceField} current value:`, values[sourceField]);
    
    const sourceValue = values[sourceField] as string | undefined;
    const currentValue = values[targetField] as
      | { id: string; value: string }
      | null
      | undefined;

    // Add critical timing check
    logger.critical(`sourceValue extracted: "${sourceValue}" (type: ${typeof sourceValue}, length: ${sourceValue?.length})`);

    logger.debug(`filterComboBoxByCountyFIPS called:`, {
      targetField,
      sourceField,
      sourceValue,
      sourceValueType: typeof sourceValue,
      sourceValueLength: sourceValue?.length,
      first5Digits: sourceValue?.substring(0, 5),
      timestamp: new Date().toISOString(),
    });

    logger.debug(
      `filterComboBoxByCountyFIPS: field=${targetField}, zip=${sourceValue}, currentValue=`,
      currentValue
    );

    const targetElement = document.getElementById(targetField);
    if (!targetElement) {
      logger.error(`Target field ${targetField} not found in DOM`);
      return;
    }

    // Helper function to set loading state
    const setLoadingState = (isLoading: boolean) => {
      logger.debug(
        `setLoadingState called with isLoading=${isLoading} for field ${targetField}`
      );

      // Update the field disable state properly through context
      if (setAppContext) {
        setAppContext((prev) => {
          const newContext = cloneDeep(prev);
          newContext.fieldDisable[targetField] = isLoading;
          logger.debug(
            `Updated fieldDisable[${targetField}] = ${isLoading} via setAppContext`
          );
          return newContext;
        });
      } else {
        // Fallback: directly mutate if setAppContext is not available
        logger.warn(
          `setAppContext not available, falling back to direct mutation for loading state`
        );
        if (appContext.fieldDisable) {
          appContext.fieldDisable[targetField] = isLoading;
        }
      }

      // Dispatch loading state event
      logger.debug(
        `Dispatching loadingStateChanged event to document with isLoading=${isLoading} for target field ${targetField}`
      );
      document.dispatchEvent(
        new CustomEvent("loadingStateChanged", {
          detail: {
            isLoading: isLoading,
            targetField: targetField,
          },
        })
      );

      logger.debug(
        `Set loading state for ${targetField}: ${isLoading}, setAppContext available: ${!!setAppContext}`
      );
    };

    // Helper function to update options in context properly
    const updateOptionsInContext = (
      newOptions: { id: string; value: string }[],
      description: string
    ) => {
      if (setAppContext) {
        setAppContext((prev) => {
          const newContext = cloneDeep(prev);
          
          // Update fieldOptions
          newContext.fieldOptions ??= {};
          newContext.fieldOptions[targetField] = newOptions;
          
          // Update autoCompleteOptions
          newContext.autoCompleteOptions ??= {};
          newContext.autoCompleteOptions[targetField] ??= { 
            originalOptions: [], 
            currentOptions: [] 
          };
          newContext.autoCompleteOptions[targetField].currentOptions = newOptions;
          
          logger.debug(`Updated context via setAppContext: ${description} - ${newOptions.length} options`);
          return newContext;
        });
      } else {
        // Fallback: direct mutation with warning
        logger.warn(`setAppContext not available, falling back to direct mutation for ${description}`);
        if (appContext.fieldOptions) {
          appContext.fieldOptions[targetField] = newOptions;
        }
        if (appContext.autoCompleteOptions?.[targetField]) {
          appContext.autoCompleteOptions[targetField].currentOptions = newOptions;
        }
      }
    };

    // Handle invalid or empty ZIP code
    if (
      !sourceValue ||
      typeof sourceValue !== "string" ||
      sourceValue.length < 5
    ) {
      logger.critical(`ZIP validation FAILED - this might be a timing issue!`);
      logger.critical(`sourceValue: "${sourceValue}", length: ${sourceValue?.length}`);
      logger.critical(`typeof sourceValue: ${typeof sourceValue}`);
      logger.critical(`Boolean checks: !sourceValue=${!sourceValue}, typeof!==string=${typeof sourceValue !== "string"}, length<5=${(sourceValue?.length ?? 0) < 5}`);
      
      logger.info(
        `Invalid or no ZIP: restoring original options for ${targetField}, sourceValue=${sourceValue}`
      );

      // Clear loading state immediately for invalid ZIP
      setLoadingState(false);

      // Get the original options
      let originalOptions: { id: string; value: string }[] = [];

      if (appContext.autoCompleteOptions?.[targetField]?.originalOptions) {
        originalOptions =
          appContext.autoCompleteOptions[targetField].originalOptions;
        logger.debug(
          `Found ${originalOptions.length} original options from autoCompleteOptions`
        );
      } else if (appContext.fieldOptions?.[targetField]) {
        originalOptions = [...appContext.fieldOptions[targetField]];
        logger.debug(
          `Found ${originalOptions.length} original options from fieldOptions`
        );
      } else {
        logger.warn(
          `No original options found for ${targetField} - this might be a problem`
        );
      }

      logger.debug(
        `Restoring ${originalOptions.length} original options for ${targetField}`
      );

      // Update context with original options using proper React state management
      updateOptionsInContext(originalOptions, "restore original options");

      // Dispatch event to notify the component about restored options
      const targetElement = document.getElementById(targetField);
      if (targetElement) {
        // Log more details about the specific target field we're working with
        if (targetField === "13118") {
          logger.important(
            `Restoring ${originalOptions.length} original options for locality field (13118)`
          );

          // Log a few examples to verify the options format
          if (originalOptions.length > 0) {
            logger.debug(
              `Sample locality options being restored:`,
              originalOptions.slice(0, 3)
            );
          } else {
            logger.warn(
              `No original options available for locality field - this is a problem!`
            );
          }
        }

        logger.debug(
          `Dispatching optionsChanged event with ${originalOptions.length} restored options for ${targetField}`
        );

        // Dispatch the event with the original options
        document.dispatchEvent(
          new CustomEvent("optionsChanged", {
            detail: {
              targetField: targetField,
              options: originalOptions,
              resetValue: false, // Don't reset value when restoring options
            },
          })
        );

        // Verify the event was dispatched by checking the DOM element
        logger.debug(`Event dispatched to element:`, targetElement.id);
      } else {
        logger.error(
          `Failed to find target element with ID ${targetField} in DOM to dispatch event`
        );
      }

      return;
    }

    // Filter options using valid ZIP code
    const zipCode = sourceValue.substring(0, 5);
    logger.info(
      `Filtering options for ${targetField} using ZIP ${zipCode}`
    );

    // Set loading state before making API call
    setLoadingState(true);

    // Add 5-second delay to test cursor change visibility
    logger.debug(`Starting 5-second delay to test cursor change...`);

    // Use setTimeout to add delay before making the API call
    logger.debug(
      `5-second delay complete, proceeding with FIPS filtering...`
    );

    store
      .dispatch(fetchFipsCodes(zipCode))
      .then((response: any) => {
        // Ensure fieldOptions is initialized
        if (!appContext.fieldOptions) {
          logger.error(
            `fieldOptions undefined during FIPS processing. Initializing...`
          );

          // Clear loading state and return
          setLoadingState(false);

          // Initialize fieldOptions if not present
          if (setAppContext) {
            setAppContext((prev) => ({
              ...prev,
              fieldOptions: prev.fieldOptions || {},
            }));
          }
          return;
        }

        if (!response?.success || !response.countyfips?.length) {
          logger.warn(
            `No valid FIPS codes for ZIP ${zipCode} - showing empty list`
          );

          // Clear loading state
          setLoadingState(false);

          // Show empty list when no FIPS codes are returned
          const emptyOptions: { id: string; value: string }[] = [];

          // Update context with empty options using proper React state management
          updateOptionsInContext(emptyOptions, "set empty options for no FIPS codes");

          // Reset value if needed with empty options
          resetValueIfInvalid(currentValue, emptyOptions);

          // Dispatch event with empty options
          document.dispatchEvent(
            new CustomEvent("optionsChanged", {
              detail: {
                targetField: targetField,
                options: emptyOptions,
                resetValue: !isValidOption(currentValue, emptyOptions),
              },
            })
          );
          return;
        }

        const fipsCodes = response.countyfips;

        // Ensure we have fieldOptions and the specific field options
        if (!appContext.fieldOptions?.[targetField]) {
          logger.error(`Field options for ${targetField} not found`);
          // Clear loading state before returning
          setLoadingState(false);
          return;
        }

        const originalOptions = appContext.autoCompleteOptions?.[targetField]?.originalOptions;

        // Log original options to verify we have them
        logger.debug(
          `Original options for ${targetField}: ${originalOptions.length} options`,
          originalOptions.slice(0, 5) // Log just the first few to avoid console overload
        );

        // Log FIPS codes we're filtering with
        logger.debug(`Filtering with FIPS codes:`, fipsCodes);

        // Check if options have the expected format with FIPS code at the end
        const sampleOptions = originalOptions.slice(0, 5); // Check just a few
        const fipsRegex = /- (\d+)$/;
        const formatCheck = sampleOptions.map((option) => {
          const match = fipsRegex.exec(option.value);
          return {
            value: option.value,
            hasFipsFormat: !!match,
            extractedFips: match ? match[1] : "none",
          };
        });
        logger.debug(
          `FIPS format check for sample options:`,
          formatCheck
        );

        const filteredOptions = originalOptions.filter(
          (option: { id: string; value: string }) => {
            if (!option?.value) return false;
            const fipsMatch = fipsRegex.exec(option.value);
            const matches = fipsMatch && fipsCodes.includes(fipsMatch[1]);
            // Log each option's matching status for debugging
            if (fipsMatch) {
              logger.debug(
                `Option "${option.value}" FIPS: ${fipsMatch[1]}, Match: ${matches}`
              );
            } else {
              logger.debug(`Option "${option.value}" has no FIPS code`);
            }
            return matches;
          }
        );

        logger.debug(
          `Filtered options for ${targetField}:`,
          filteredOptions
        );

        // Always use filtered options, even if empty
        const newOptions = [...filteredOptions];

        // Log specifically for empty filtered results
        if (newOptions.length === 0) {
          logger.info(
            `FIPS filter resulted in zero matching options for ${targetField} using FIPS ${fipsCodes.join(
              ", "
            )}. Keeping empty list as requested.`
          );
        }

        // Check if we need to update options
        const needsUpdate =
          appContext.fieldOptions &&
          !isEqual(appContext.fieldOptions[targetField], newOptions);

        if (needsUpdate) {
          // Update context with filtered options using proper React state management
          updateOptionsInContext(newOptions, "set filtered options");

          // Reset value if it's no longer valid with the new options
          resetValueIfInvalid(currentValue, newOptions);

          // Log that we're dispatching the event
          logger.debug(
            `Dispatching optionsChanged event for ${targetField} with ${newOptions.length} options`
          );

          // Dispatch event to notify the component about the options change
          document.dispatchEvent(
            new CustomEvent("optionsChanged", {
              detail: {
                targetField: targetField,
                options: newOptions,
                resetValue: !isValidOption(currentValue, newOptions),
              },
            })
          );
        }

        // Clear loading state after filtering is complete
        setLoadingState(false);
      })
      .catch((error: any) => {
        logger.error(`Failed to fetch FIPS codes: ${error}`);

        // Clear loading state on error
        setLoadingState(false);

        // Show empty list on fetch error
        const emptyOptions: { id: string; value: string }[] = [];

        // Check if we need to update to empty options
        if (
          appContext.fieldOptions &&
          !isEqual(appContext.fieldOptions[targetField], emptyOptions)
        ) {
          logger.debug(
            `API error - showing empty options list for ${targetField}`
          );

          // Update context with empty options using proper React state management
          updateOptionsInContext(emptyOptions, "set empty options for API error");

          // Reset value if it's no longer valid
          resetValueIfInvalid(currentValue, emptyOptions);

          // Dispatch event with empty options
          document.dispatchEvent(
            new CustomEvent("optionsChanged", {
              detail: {
                targetField: targetField,
                options: emptyOptions,
                resetValue: !isValidOption(currentValue, emptyOptions),
              },
            })
          );
        }
      });

    function isValidOption(
      value: { id: string; value: string } | null | undefined,
      options: { id: string; value: string }[]
    ): boolean {
      if (!value?.id || !value?.value) return false;
      return options.some(
        (option) => option.id === value.id && option.value === value.value
      );
    }

    function resetValueIfInvalid(
      value: { id: string; value: string } | null | undefined,
      options: { id: string; value: string }[]
    ) {
      if (value && !isValidOption(value, options)) {
        logger.info(
          `Resetting value for ${targetField} as it is not in new options`
        );
        setValue(targetField, null);
      }
    }

    function dispatchInitialization(field: string, source: string) {
      if (appContext.initializationFlags?.[`${field}_isInitializing`]) {
        logger.info(`Initialization already in progress for ${field}`);
        return;
      }
      appContext.initializationFlags![`${field}_isInitializing`] = true;

      const initEvent = new CustomEvent("initializeOptions", {
        detail: { field },
      });
      document.dispatchEvent(initEvent);

      const handleOptionsAvailable = (event: any) => {
        if (event.detail?.field === field) {
          logger.info(`Options available, retrying filtering`);
          document.removeEventListener(
            "optionsAvailable",
            handleOptionsAvailable
          );
          delete appContext.initializationFlags![`${field}_isInitializing`];
          setTimeout(() => filterComboBoxByCountyFIPS(field, source), 100);
        }
      };

      document.addEventListener("optionsAvailable", handleOptionsAvailable);
      setTimeout(() => {
        document.removeEventListener(
          "optionsAvailable",
          handleOptionsAvailable
        );
        delete appContext.initializationFlags![`${field}_isInitializing`];
      }, 5000);
    }
  };

  map.set("calculateAge", calculateAge);
  map.set("hideFieldsWhen", hideFieldsWhen);
  map.set("disableEnableFieldsWhen", disableEnableFieldsWhen);
  map.set("setTextFieldValueWhen", setTextFieldValueWhen);
  map.set("toggleCheckbox", toggleCheckbox);
  map.set("resetFields", resetFields);
  map.set("resetErrorState", resetErrorState);
  map.set("copyFieldsWhen", copyFieldsWhen);
  map.set("disableEnableStepWhen", disableEnableStepWhen);
  map.set("setMinimumNumberOfRows", setMinimumNumberOfRows);
  map.set("loadForm", loadForm);
  map.set("clearListView", clearListView);
  map.set("setCompleteStepState", setCompleteStepState);
  map.set("setValueWhen", setValueWhen);
  map.set("filterComboBoxByCountyFIPS", filterComboBoxByCountyFIPS);

  const func: (...args: any[]) => void = map.get(funcName) ?? (() => {});
  func(...args, values, setValue);
};
